import { timeZonesNames } from '@vvo/tzdb';

export const DEFAULT_TIMEZONE = 'Europe/Amsterdam';

// Bundled tzdb snapshot rather than Intl.supportedValuesOf('timeZone'): that
// API only shipped in Safari/WebKit 15.4, and this app's iOS deployment
// target is 15.0 - a bundled list works identically on every platform with no
// runtime feature detection, and no degraded fallback for unsupported ones.
const TIMEZONE_OPTIONS = ['UTC', ...timeZonesNames];

/** Every IANA zone name available to the timezone picker. */
export const getTimezoneOptions = (): string[] => TIMEZONE_OPTIONS;

/** The device's current IANA zone, used only to pre-highlight a sensible default. */
export const getDetectedTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/**
 * Today's calendar date ("YYYY-MM-DD") in the given IANA zone. `en-CA`
 * formats dates this way natively - no manual offset math, no device-timezone
 * dependency.
 */
export const todayInTimezone = (timezone: string, now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    now,
  );

/**
 * Adds `days` calendar days to a "YYYY-MM-DD" string, staying in UTC
 * throughout so the result never shifts with the device's own timezone/DST.
 */
export const addDaysToDateString = (dateString: string, days: number): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const WALL_CLOCK_PARTS: Intl.DateTimeFormatOptions = {
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

/** How far the zone's wall clock is ahead of UTC at that instant, in milliseconds. */
const zoneOffsetAt = (instant: number, timezone: string): number => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { ...WALL_CLOCK_PARTS, timeZone: timezone })
      .formatToParts(new Date(instant))
      .map((part) => [part.type, part.value]),
  );
  const wallClock = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return wallClock - (instant - (instant % 1000));
};

/**
 * The UTC instant of a wall-clock date ("YYYY-MM-DD") and time ("HH:mm") in an IANA zone, as an
 * ISO string: 09:00 in a store is 09:00 there, whatever the device's own zone. The offset is read
 * twice so a time right after a DST change lands on the right side of it.
 */
export const zonedDateTimeToIso = (date: string, time: string, timezone: string): string => {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute);
  const firstGuess = asUtc - zoneOffsetAt(asUtc, timezone);
  return new Date(asUtc - zoneOffsetAt(firstGuess, timezone)).toISOString();
};
